import React from 'react';
import { useTranslation } from '../i18n';
import Icon, { IconName } from './Icon';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  loadingText?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'danger',
  loading = false,
  loadingText
}) => {
  const { t } = useTranslation();

  const getIconName = (): IconName => {
    switch (type) {
      case 'danger':
        return 'warning';
      case 'warning':
        return 'retry';
      case 'info':
        return 'info';
      default:
        return 'question';
    }
  };

  const getTypeClass = () => {
    switch (type) {
      case 'danger':
        return 'confirm-dialog-danger';
      case 'warning':
        return 'confirm-dialog-warning';
      case 'info':
        return 'confirm-dialog-info';
      default:
        return 'confirm-dialog-danger';
    }
  };

  const footer = (
    <div className="confirm-dialog-actions">
      <button onClick={onCancel} className="btn-cancel" disabled={loading}>
        {cancelText || t('common.cancel')}
      </button>
      <button onClick={onConfirm} className={`btn-confirm btn-confirm-${type}`} disabled={loading}>
        {loading ? (
          <>
            <LoadingSpinner size="small" color="white" hideMessage />
            <span>{loadingText || t('common.loading')}</span>
          </>
        ) : (
          confirmText || t('common.confirm')
        )}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      disableClose={loading}
      size="sm"
      hideHeader
      contentClassName={`confirm-dialog ${getTypeClass()}`}
      footer={footer}
      ariaLabelledBy="confirm-dialog-title"
    >
      <div className="confirm-dialog-header">
        <span className="confirm-dialog-icon">
          <Icon name={getIconName()} />
        </span>
        <h3 id="confirm-dialog-title" className="confirm-dialog-title">{title}</h3>
      </div>
      <div className="confirm-dialog-body">
        <p className="confirm-dialog-message">{message}</p>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
